import { Box, Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

import { useEffect, useRef, useState } from 'react';
import EventSubscription from './eventSubscription';
import ErrorMessage from '@/components/errorMessage';
import { eventNameEquals } from '@/utils';

type Props = {
  entity: any;
  maxHeight?: number;
  eventSources: any[];
  onAddSubscription: (eventSubscription: any) => void;
  onEditSubscription: (oldSubscription: any, newSubscription: any) => void;
  onDeleteSubscription: (eventSubscription: any) => void;
};

const EntityEventSubscriptions = ({
  entity = {},
  eventSources = [],
  maxHeight,
  onAddSubscription,
  onEditSubscription,
  onDeleteSubscription,
}: Props) => {
  const newEventRef = useRef<null | HTMLDivElement>();

  const [newEvent, setNewEvent] = useState(false);
  const [newSubscriptionError, setNewSubscriptionError] = useState('');
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    if (newEventRef.current && newEvent) {
      newEventRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [newEventRef.current, newEvent]);

  const addEvent = () => {
    setNewSubscriptionError('');
    setNewEvent(true);
  };

  const addSubscription = (eventSubscription: any) => {
    const es = (entity.eventSubscriptions || []).find(
      (es: any) =>
        es.eventSourceId === eventSubscription.eventSourceId &&
        es.eventSourceType === eventSubscription.eventSourceType &&
        es.eventSourceDefId === eventSubscription.eventSourceDefId &&
        eventNameEquals(es.eventName, eventSubscription.eventName),
    );
    if (!es) {
      onAddSubscription(eventSubscription);
      setNewEvent(false);
      setNewSubscriptionError('');
    } else {
      setNewSubscriptionError('Already exists');
    }
  };

  const editSubscription = (oldSubscription: any, newSubscription: any) =>
    onEditSubscription(oldSubscription, newSubscription);
  const deleteSubscription = (eventSubscription: any) => onDeleteSubscription(eventSubscription);
  const cancelAdd = () => {
    setNewSubscriptionError('');
    setNewEvent(false);
  };

  return (
    <Box sx={{ height: maxHeight ? `${maxHeight}px` : '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction={'row'} spacing={1} justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button
          variant="contained"
          size="small"
          color="secondary"
          onClick={() => addEvent()}
          disabled={newEvent}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            color: 'text.primary',
          }}
        >
          Add new subscription
        </Button>
      </Stack>
      <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 500 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ borderRight: '1px solid', borderColor: 'divider', width: '40%' }}>
                  Event source
                </TableCell>
                <TableCell sx={{ borderRight: '1px solid', borderColor: 'divider', width: '40%' }}>Event</TableCell>
                <TableCell sx={{ width: '20%' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(entity.eventSubscriptions || []).map((subscription: any, idx: any) => (
                <EventSubscription
                  key={idx}
                  entity={entity}
                  eventSubscription={subscription}
                  eventSources={eventSources}
                  onAdd={addSubscription}
                  onEdit={editSubscription}
                  onDelete={deleteSubscription}
                  onCancelAdd={cancelAdd}
                />
              ))}
              {newEvent && (
                <EventSubscription
                  entity={entity}
                  eventSources={eventSources}
                  onAdd={addSubscription}
                  onEdit={editSubscription}
                  onDelete={deleteSubscription}
                  onCancelAdd={cancelAdd}
                  errror={newSubscriptionError}
                />
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {newSubscriptionError && <ErrorMessage error={{ message: newSubscriptionError }} />}
      </Box>
    </Box>
  );
};

export default EntityEventSubscriptions;
